// Screen placement of the floating labels: keeps every label inside the viewport and slides
// movable labels (production figures) sideways off fixed ones (price tags, landmarks), so two
// neighbouring tiles never print one label over the other, for example in the "Cenital" view.

export interface LabelBox {
  /** Horizontal centre of the anchor on screen, in CSS pixels. */
  readonly x: number;
  /** Bottom edge of the label (the anchor point), in CSS pixels. */
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Fixed labels keep their place; movable ones are moved out of their way. */
  readonly movable: boolean;
}

/** Gap kept between a label and the screen edge, in CSS pixels. */
export const LABEL_EDGE = 6;
/** Gap kept between two labels that had to be separated, in CSS pixels. */
export const LABEL_GAP = 4;

function overlaps(ax: number, a: LabelBox, bx: number, b: LabelBox): boolean {
  return Math.abs(ax - bx) * 2 < a.width + b.width + LABEL_GAP * 2 && a.y - a.height < b.y && b.y - b.height < a.y;
}

function clampX(x: number, box: LabelBox, viewWidth: number): number {
  const half = box.width / 2 + LABEL_EDGE;
  return Math.min(viewWidth - half, Math.max(half, x));
}

/** Returns the horizontal centre of each label after clamping and separating them. */
export function layoutLabels(boxes: readonly LabelBox[], viewWidth: number): number[] {
  const xs = boxes.map((b) => clampX(b.x, b, viewWidth));
  const placed: number[] = [];
  for (let i = 0; i < boxes.length; i++) if (!boxes[i]?.movable) placed.push(i);
  for (let i = 0; i < boxes.length; i++) {
    const box = boxes[i];
    if (!box?.movable) continue;
    let x = xs[i] ?? box.x;
    // A few passes settle a label squeezed between two others; it never loops forever.
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const j of placed) {
        const other = boxes[j];
        const ox = xs[j];
        if (!other || ox === undefined || !overlaps(x, box, ox, other)) continue;
        const push = (box.width + other.width) / 2 + LABEL_GAP;
        x = clampX(x <= ox ? ox - push : ox + push, box, viewWidth);
        moved = true;
      }
      if (!moved) break;
    }
    xs[i] = x;
    placed.push(i);
  }
  return xs;
}

/** A point on screen, in CSS pixels. */
export interface ScreenSpot {
  readonly x: number;
  readonly y: number;
}

/** Gap kept between a door and its figure, in CSS pixels. */
export const DOOR_LABEL_GAP = 6;

/**
 * Where a door's figure goes: beside the door, pushed out along the screen direction from the door
 * (`at`) to a point further out of the tile (`out`) until the whole label clears the door, so it
 * never covers the belts inside, whichever way the camera looks. Returns the label's top-left corner.
 */
export function besideDoor(at: ScreenSpot, out: ScreenSpot, width: number, height: number): { left: number; top: number } {
  const dx = out.x - at.x;
  const dy = out.y - at.y;
  const len = Math.hypot(dx, dy);
  const ux = len > 1e-6 ? dx / len : 0;
  const uy = len > 1e-6 ? dy / len : -1;
  const reach = Math.abs(ux) * (width / 2) + Math.abs(uy) * (height / 2) + DOOR_LABEL_GAP;
  return { left: at.x + ux * reach - width / 2, top: at.y + uy * reach - height / 2 };
}
